import Component from '@glimmer/component';
import { PhReceipt, PhClock, PhWarning } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import courseCashMark from 'tio-common/helpers/course-cash-mark';
import type { CourseCashTransaction } from 'tio-common/types/course-cash-transaction';

interface ReceiptBannerSignature {
  Args: {
    transactions: CourseCashTransaction[];
    onUploadReceipt: (transaction: CourseCashTransaction) => void;
  };
  Element: HTMLElement;
}

export default class CourseCashReceiptBanner extends Component<ReceiptBannerSignature> {
  @service declare intl: IntlService;

  get unverifiedTransactions() {
    return this.args.transactions.filter(
      (t) =>
        t.receiptStatus === 'needed' ||
        t.receiptStatus === 'rejected' ||
        t.receiptStatus === 'changes_needed'
    );
  }

  get needsReceiptTransactions() {
    return this.unverifiedTransactions.filter((t) => t.receiptStatus === 'needed');
  }

  get changesNeededTransactions() {
    return this.unverifiedTransactions.filter((t) => t.receiptStatus === 'changes_needed');
  }

  get rejectedTransactions() {
    return this.unverifiedTransactions.filter((t) => t.receiptStatus === 'rejected');
  }

  get transactionsByDueDate() {
    const groups = new Map<string, CourseCashTransaction[]>();

    this.needsReceiptTransactions.forEach((transaction) => {
      const dueDate = transaction.receiptDueDate;
      if (!dueDate) return;

      if (!groups.has(dueDate)) {
        groups.set(dueDate, []);
      }
      groups.get(dueDate)!.push(transaction);
    });

    return Array.from(groups.entries())
      .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
      .map(([dueDate, transactions]) => ({
        dueDate,
        transactions: transactions.sort(
          (a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()
        ),
        daysUntilDue: this.calculateDaysUntilDue(dueDate),
      }));
  }

  calculateDaysUntilDue(dueDate: string) {
    // Parse dates in local timezone to avoid UTC conversion issues
    // Dates are in YYYY-MM-DD format from the backend
    const due = new Date(dueDate + 'T00:00:00');
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to start of day for comparison

    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  formatDate = (dateString: string) => {
    // Parse in local timezone to avoid UTC date shift
    const date = new Date(dateString + 'T00:00:00');
    return this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  formatAmount = (amount: number) => {
    // Amount comes from transaction-adapter in cents
    return this.intl.formatNumber(amount / 100, {
      style: 'currency',
      currency: 'USD',
    });
  };

  isDaySingular = (days: number) => days === 1;

  formatDueDateText = (days: number) => {
    const absDays = Math.abs(days);
    const dayLabel = this.intl.t(
      absDays === 1 ? 'tas.course_cash.receipt_due.day' : 'tas.course_cash.receipt_due.days'
    );

    if (days < 0) {
      return this.intl.t('tas.course_cash.receipt_due.past_due', {
        days: absDays,
        dayLabel,
      });
    } else if (days === 0) {
      return this.intl.t('tas.course_cash.receipt_due.due_today');
    } else {
      return this.intl.t('tas.course_cash.receipt_due.due_in', {
        days,
        dayLabel,
      });
    }
  };

  <template>
    {{#if this.unverifiedTransactions.length}}
      <div class="tio-bg-brand-soft tio-border-brand border-2 rounded-2xl p-4 mb-4" ...attributes>
        <div class="tio-col-extra-tight">
          {{! Header }}
          <div class="tio-row-tight">
            <PhReceipt @size="20" @weight="regular" class="tio-fg-primary" />
            <p class="tio-fg-primary tio-text-base-semibold leading-snug">
              {{t "tas.course_cash.receipt_banner.receipts_needed"}}
            </p>
          </div>

          {{! Description }}
          <p class="tio-fg-secondary tio-text-base-medium leading-relaxed">
            {{courseCashMark (t "tas.course_cash.receipt_banner.description")}}
          </p>

          {{! Rejected receipts section }}
          {{#if this.rejectedTransactions.length}}
            <div class="tio-col pt-2">
              <div class="tio-row-tight">
                <PhWarning @size="24" @weight="regular" class="tio-fg-danger" />
                <p class="tio-fg-primary tio-text-base-bold leading-snug">
                  {{t "tas.course_cash.receipt_banner.receipt_rejected"}}
                </p>
              </div>

              {{#each this.rejectedTransactions as |transaction|}}
                <div class="tio-row-between w-full">
                  <div class="tio-row-extra-loose tio-fg-primary text-base">
                    <p class="font-normal leading-snug">
                      {{this.formatDate transaction.transactionDate}}
                    </p>
                    <p class="font-normal leading-snug">
                      {{transaction.merchantName}}
                    </p>
                    <p class="font-medium leading-snug">
                      {{this.formatAmount transaction.amount}}
                    </p>
                    <button
                      type="button"
                      class="font-semibold underline cursor-pointer"
                      {{on "click" (fn @onUploadReceipt transaction)}}
                    >
                      {{t "tas.course_cash.receipt_banner.reupload_receipt"}}
                    </button>
                  </div>
                </div>
              {{/each}}
            </div>
          {{/if}}

          {{! Changes needed section }}
          {{#if this.changesNeededTransactions.length}}
            <div class="tio-col pt-2">
              <div class="tio-row-tight">
                <PhWarning @size="24" @weight="regular" class="tio-fg-warning" />
                <p class="tio-fg-primary tio-text-base-bold leading-snug">
                  {{t "tas.course_cash.receipt_banner.changes_needed"}}
                </p>
              </div>

              {{#each this.changesNeededTransactions as |transaction|}}
                <div class="tio-row-between w-full">
                  <div class="tio-row-extra-loose tio-fg-primary text-base">
                    <p class="font-normal leading-snug">
                      {{this.formatDate transaction.transactionDate}}
                    </p>
                    <p class="font-normal leading-snug">
                      {{transaction.merchantName}}
                    </p>
                    <p class="font-medium leading-snug">
                      {{this.formatAmount transaction.amount}}
                    </p>
                    <button
                      type="button"
                      class="font-semibold underline cursor-pointer"
                      {{on "click" (fn @onUploadReceipt transaction)}}
                    >
                      {{t "tas.course_cash.receipt_banner.reupload_receipt"}}
                    </button>
                  </div>
                </div>
              {{/each}}
            </div>
          {{/if}}

          {{! Transactions by due date (needed receipts) }}
          {{#if this.needsReceiptTransactions.length}}
            <div class="tio-col pt-2">
              {{#each this.transactionsByDueDate as |group|}}
                <div class="tio-col">
                  {{! Due date header }}
                  <div class="tio-row-tight">
                    <PhClock @size="24" @weight="regular" class="tio-fg-primary" />
                    <p class="tio-fg-primary tio-text-base-bold leading-snug">
                      {{this.formatDueDateText group.daysUntilDue}}
                    </p>
                  </div>

                  {{! Transactions }}
                  {{#each group.transactions as |transaction|}}
                    <div class="tio-row-between w-full">
                      <div class="tio-row-extra-loose tio-fg-primary text-base">
                        <p class="font-normal leading-snug">
                          {{this.formatDate transaction.transactionDate}}
                        </p>
                        <p class="font-normal leading-snug">
                          {{transaction.merchantName}}
                        </p>
                        <p class="font-medium leading-snug">
                          {{this.formatAmount transaction.amount}}
                        </p>
                        <button
                          type="button"
                          class="font-semibold underline cursor-pointer"
                          {{on "click" (fn @onUploadReceipt transaction)}}
                        >
                          {{t "tas.course_cash.receipt_banner.upload_receipt"}}
                        </button>
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{/each}}
            </div>
          {{/if}}
        </div>
      </div>
    {{/if}}
  </template>
}
